import * as React from 'react';
import * as styles from './FullWidthCarousel.module.scss';
import { Container } from '../container/Container';
import { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { navigate } from 'gatsby';
import { routes } from '@/routes/routes';
import { ProjectFullCard } from '../projectFullCard/ProjectFullCard';
import { IGatsbyImageData } from 'gatsby-plugin-image';

const RESET_CAROUSEL_TIMEOUT = 500;

export type CarouselHandle = {
  prev: () => void;
  next: () => void;
};
type ItemType = {
  id: string;
  title: string;
  imageUrl?: string;
  altText?: string | null;
  gatsbyImage?: IGatsbyImageData;
  subtitle: string;
  tags: string[];
};

export type FullWidthCarouselProps = {
  items: ItemType[];
};

export const FullWidthCarousel = React.forwardRef<CarouselHandle, FullWidthCarouselProps>(({ items }, ref) => {
  const [currentIndex, setCurrentIndex] = useState(items.length);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const totalItems = items.length;

  const updateIndex = React.useCallback((newIndex: number) => {
    setIsAnimating(true);
    setCurrentIndex(newIndex);
  }, []);

  const prev = React.useCallback(() => {
    if (isTransitioning) return;
    updateIndex((currentIndex - 1 + totalItems * 3) % (totalItems * 3));
  }, [updateIndex, totalItems, currentIndex, isTransitioning]);

  const next = React.useCallback(() => {
    if (isTransitioning) return;
    updateIndex((currentIndex + 1) % (totalItems * 3));
  }, [updateIndex, totalItems, currentIndex, isTransitioning]);

  const handlers = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: prev,
  });

  React.useImperativeHandle(
    ref,
    () => ({
      prev,
      next,
    }),
    [prev, next],
  );

  useEffect(() => {
    if (currentIndex === totalItems * 2 || currentIndex === 0) {
      setTimeout(() => {
        setIsAnimating(false);
        setCurrentIndex(totalItems);
      }, RESET_CAROUSEL_TIMEOUT);
    }

    // Opposite condition means that means we have updated the index in the condition above before (so transition already happened)
    if (currentIndex !== totalItems) {
      setIsTransitioning(true);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }
  }, [currentIndex, totalItems]);

  const duplicateItems = [...items, ...items, ...items];

  const handleItemClick = (item: ItemType, index: number) => {
    if (index === currentIndex) {
      navigate(
        routes.caseStudy({
          title: item.title.replace(/\s+/g, '-').replace(/\.+$/, '').replace(':', '').replace("'", '').toLowerCase(),
        }),
      );
    } else if (index === currentIndex - 1) {
      prev();
    } else if (index === currentIndex + 1) {
      next();
    }
  };

  if (totalItems === 1) {
    return (
      <div className={styles.carouselContainer}>
        <Container>
          <div className={styles.singleItemContainer} onClick={() => handleItemClick(items[0], currentIndex)}>
            <ProjectFullCard
              title={items[0].title}
              imageUrl={items[0].imageUrl}
              gatsbyImage={items[0].gatsbyImage}
              altText={items[0].altText}
              subtitle={items[0].subtitle}
              tags={[...new Set(items[0].tags)]}
              isActive={true}
              hideAnimation={true}
            />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={styles.carouselContainer}>
      <Container>
        <div
          {...handlers}
          className={styles.carousel}
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: isAnimating ? 'transform 0.5s ease-in-out' : 'none',
          }}
        >
          {duplicateItems.map((item, index) => (
            <div
              key={index}
              className={styles.carouselItem}
              aria-hidden={index !== currentIndex}
              onClick={() => handleItemClick(item, index)}
            >
              <ProjectFullCard
                title={item.title}
                imageUrl={item.imageUrl}
                gatsbyImage={item.gatsbyImage}
                subtitle={item.subtitle}
                tags={[...new Set(item.tags)]}
                isActive={index === currentIndex}
                hideAnimation={!isAnimating}
              />
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
});
